/**
 * GuestCounter.jsx
 * Created by michaelbray on 9/22/17.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import { lowerCase } from 'lodash';

import GuestCounterRow from './GuestCounterRow';

import * as Icons from '../icons/Icons';

const propTypes = {
    title: PropTypes.string,
    guestTypes: PropTypes.array,
    maxTotalGuests: PropTypes.number,
    maxTotalTitle: PropTypes.string,
    onCountChange: PropTypes.func,
    value: PropTypes.shape({}),
    validationErrorClass: PropTypes.string,
    disabled: PropTypes.bool
};

const defaultProps = {
    title: "Guests",
    disabled: false,
    maxTotalGuests: 0,
    validationErrorClass: '',
    guestTypes: [
        {
            key: "adult",
            type: "Adult",
            description: "ages 13+"
        },
        {
            key: "youth",
            type: "Youth",
            description: "ages 9 - 12"
        },
        {
            key: "access",
            type: "Access",
            description: "no age required"
        },
        {
            key: "senior",
            type: "Senior",
            description: "ages 65+"
        }
    ]
};

export const getWeight = (guestType) => {
    if (typeof guestType.weight === 'number') {
        return guestType.weight;
    }
    return 1;
};

export default class GuestCounter extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            open: false,
            counts: {}
        };

        this.toggleCounter = this.toggleCounter.bind(this);
        this.closeCounter = this.closeCounter.bind(this);
        this.changeCount = this.changeCount.bind(this);
    }

    componentWillMount() {
        this.setState({
            counts: this.props.value || this.populateCounters(this.props.guestTypes)
        });
    }

    componentDidMount() {
        window.addEventListener('click', this.toggleCounter);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.value !== this.props.value
            || nextProps.guestTypes !== this.props.guestTypes) {
            this.setState({
                counts: nextProps.value || this.populateCounters(nextProps.guestTypes)
            });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('click', this.toggleCounter);
    }

    // We need to use findDOMNode for this functionality to work
    /* eslint-disable react/no-find-dom-node */
    toggleCounter(event) {
        if (this.props.disabled) {
            return;
        }

        const holder = findDOMNode(this.holder);
        const dropdown = findDOMNode(this.dropdown);

        if (!dropdown.contains(event.target) && this.state.open) {
            this.setState({
                open: false
            });
        }
        else if (!dropdown.contains(event.target) && holder.contains(event.target)) {
            event.preventDefault();

            this.setState({
                open: !this.state.open
            });
        }
    }
    /* eslint-enable react/no-find-dom-node */

    closeCounter() {
        this.setState({
            open: false
        });
    }

    populateCounters(guestTypes) {
        const counts = {};

        if (guestTypes.length > 0) {
            guestTypes.forEach((guestType) => {
                if (guestType.count) {
                    counts[guestType.key] = guestType.count;
                }
                else {
                    counts[guestType.key] = 0;
                }
            });
        }

        return counts;
    }

    changeCount(key, count) {
        let counts;
        const uncontrolled = !this.props.value;

        // Grab the current state
        if (uncontrolled) {
            counts = this.state.counts;
        }
        else {
            counts = { ...this.props.value };
        }

        // Update the appropriate count
        counts[key] += count;

        // Don't let counts go negative
        if (counts[key] < 0) {
            counts[key] = 0;
        }

        if (uncontrolled) {
            // Update the state
            this.setState({
                counts
            });
        }

        if (this.props.onCountChange) {
            this.props.onCountChange(counts);
        }
    }

    sumCounts() {
        const counts = this.props.value || this.state.counts;
        return this.props.guestTypes.reduce((total, guestType) => {
            return total + (counts[guestType.key] * getWeight(guestType));
        }, 0);
    }

    determinePluralLabel(count) {
        // Grab the title from props
        const title = this.props.title;

        // If the count is 1, and the title is pluralized, remove the s
        // This is obviously a very basic implementation
        if (count === 1 && title.charAt(title.length - 1).toLowerCase() === 's') {
            return title.slice(0, title.length - 1);
        }

        // Otherwise, return the plural title
        return title;
    }

    render() {
        // Generate Label for Dropdown
        let label = this.props.title;
        const totalCount = this.sumCounts();
        if (totalCount > 0) {
            label = `${totalCount} ${this.determinePluralLabel(totalCount)}`;
        }

        // If maxTotalGuests is passed in, check it against totalCount
        const maxTotalGuests = this.props.maxTotalGuests;
        let maxTotalGuestsReached = false;
        let showErrorClass = '';
        if (maxTotalGuests > 0) {
            if (totalCount >= maxTotalGuests) {
                maxTotalGuestsReached = true;
                showErrorClass = 'rec-show-error';
            }
        }

        // Format the max title
        const maxTotalTitle = ` ${lowerCase(this.props.title)}`;

        // Populate choice rows
        const counts = this.props.value || this.state.counts;
        const guestCounterRows = this.props.guestTypes.map((guestType) => (
            <GuestCounterRow
                key={`guest-counter-${guestType.key}`}
                guestType={guestType}
                count={counts[guestType.key]}
                maxTotalGuestsReached={maxTotalGuestsReached}
                changeCount={this.changeCount} />
        ));

        // Generate up or down arrow icon
        let showGuestCounterRows = 'hide';
        let selectBorder = '';
        let icon = <Icons.IconArrowDown alt="Pick a field" />;

        if (this.state.open) {
            showGuestCounterRows = '';
            selectBorder = 'open';
            icon = <Icons.IconArrowUp alt="Pick a field" />;
        }

        const { validationErrorClass } = this.props;

        return (
            <div
                className="rec-select-holder"
                ref={(holder) => {
                    this.holder = holder;
                }}>
                <button
                    className={`rec-select rec-select-guest-counter ${selectBorder} ${validationErrorClass}`}
                    title={this.props.title}
                    aria-label={this.props.title}
                    disabled={this.props.disabled}>
                    <span className="rec-select-label">
                        {label}
                    </span>
                    <span className="rec-select-icon">
                        {icon}
                    </span>
                </button>
                <div
                    className={`rec-select-options ${showGuestCounterRows}`}
                    ref={(dropdown) => {
                        this.dropdown = dropdown;
                    }}>
                    <ul className="rec-guest-counter-wrap">
                        {guestCounterRows}
                    </ul>

                    <div className={`rec-max-guests ${showErrorClass}`}>
                        <Icons.IconWarning /><p>You can select a maximum of <strong>{maxTotalGuests}</strong>{maxTotalTitle}.</p>
                    </div>

                    <div className="rec-select-option-last">
                        <button
                            className="rec-close-dropdown-button"
                            onClick={this.closeCounter}
                            aria-label="Close"
                            type="button">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

GuestCounter.propTypes = propTypes;
GuestCounter.defaultProps = defaultProps;
